import React, { useEffect, useState } from 'react';
import { FiDownload, FiShare, FiX, FiPlusSquare } from 'react-icons/fi';

const isStandaloneApp = () =>
  window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;

const isIosDevice = () => /iphone|ipad|ipod/i.test(window.navigator.userAgent);

const IosSheet = ({ onClose }) => (
  <div className="fixed inset-0 z-[80] bg-black/60 flex items-end sm:items-center justify-center p-4" onClick={onClose}>
    <div
      className="bg-slate-900 border border-slate-700 rounded-t-2xl sm:rounded-2xl p-5 w-full max-w-sm text-slate-200"
      onClick={(e) => e.stopPropagation()}
    >
      <h3 className="font-bold text-white mb-3">Add to iPhone Home Screen</h3>
      <ol className="space-y-3 text-sm text-slate-300">
        <li className="flex gap-2">
          <FiShare className="shrink-0 mt-0.5 text-teal-400" />
          <span>
            Tap the <strong className="text-white">Share</strong> button in Safari
          </span>
        </li>
        <li className="flex gap-2">
          <FiPlusSquare className="shrink-0 mt-0.5 text-teal-400" />
          <span>
            Scroll and tap <strong className="text-white">Add to Home Screen</strong>
          </span>
        </li>
      </ol>
      <button
        type="button"
        onClick={onClose}
        className="mt-4 w-full py-3 rounded-xl bg-teal-500 text-slate-950 font-semibold"
      >
        Got it
      </button>
    </div>
  </div>
);

const InstallBanner = ({ alwaysShow = false }) => {
  const [deferred, setDeferred] = useState(null);
  const [showIos, setShowIos] = useState(false);
  const [dismissed, setDismissed] = useState(() => localStorage.getItem('tech_a2hs_dismissed') === '1');
  const [showManual, setShowManual] = useState(false);

  const standalone = isStandaloneApp();
  const isIos = isIosDevice();

  useEffect(() => {
    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    window.addEventListener('beforeinstallprompt', onPrompt);
    return () => window.removeEventListener('beforeinstallprompt', onPrompt);
  }, []);

  if (standalone || (dismissed && !alwaysShow)) return null;

  const hide = () => {
    localStorage.setItem('tech_a2hs_dismissed', '1');
    setDismissed(true);
    setShowIos(false);
    setShowManual(false);
  };

  const install = async () => {
    if (deferred) {
      deferred.prompt();
      await deferred.userChoice;
      setDeferred(null);
      hide();
      return;
    }
    if (isIos) {
      setShowIos(true);
      return;
    }
    setShowManual(true);
  };

  if (!alwaysShow && !deferred && !isIos) return null;

  return (
    <>
      <div className="mb-3 p-3 rounded-xl bg-teal-500/15 border border-teal-500/30 flex items-center gap-3">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-teal-100">Add to Home Screen</p>
          <p className="text-xs text-teal-200/70">Open TaskMate Tech like an app</p>
        </div>
        <button
          type="button"
          onClick={install}
          className="shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-lg bg-teal-500 text-slate-950 text-xs font-semibold touch-manipulation"
        >
          <FiDownload size={14} />
          Add
        </button>
        <button type="button" onClick={hide} className="p-2 text-slate-400 touch-manipulation" aria-label="Dismiss">
          <FiX size={16} />
        </button>
      </div>

      {showIos && <IosSheet onClose={() => { setShowIos(false); hide(); }} />}

      {showManual && (
        <div className="fixed inset-0 z-[80] bg-black/60 flex items-end sm:items-center justify-center p-4" onClick={() => setShowManual(false)}>
          <div
            className="bg-slate-900 border border-slate-700 rounded-t-2xl sm:rounded-2xl p-5 w-full max-w-sm text-slate-200"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="font-bold text-white mb-3">Install this page</h3>
            <p className="text-sm text-slate-300">
              Open your browser menu and tap <strong className="text-white">Install app</strong> or{' '}
              <strong className="text-white">Add to Home screen</strong>.
            </p>
            <button
              type="button"
              onClick={hide}
              className="mt-4 w-full py-3 rounded-xl bg-teal-500 text-slate-950 font-semibold"
            >
              Got it
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default InstallBanner;
